import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiSearch, FiDownload, FiPrinter, FiExternalLink, FiFileText } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { useAuth } from '../../context/AuthContext';
import useReportMemory from '../../hooks/useReportMemory';
import { getTransactions } from '../../utils/transactionHelper';
import { formatAmount } from '../../utils/documentNumbering';
import { TRAN_TYPE_ROUTES } from '../../utils/navigationHelper';
import PageLayout from '../../components/PageLayout';
import {
  TableContainer, Table, Th, Tr, Td, StatusBadge, LoadingMessage, TranEmptyState
} from '../../components/SharedStyles';
import { addPrintBrand } from '../../utils/printBranding';

const STATUS_COLORS = { Draft: colors.warning.main, Posted: colors.success.main, Cancelled: colors.error.main };

const MaterialIssueReport = () => {
  const { activeCompany, activeBranch } = useAuth();
  const [savedMem, saveMemory] = useReportMemory('material-issue-report');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(savedMem ? false : true);
  const [issues, setIssues] = useState(() => savedMem?.issues ?? []);
  const [receipts, setReceipts] = useState(() => savedMem?.receipts ?? []);
  const today = new Date().toISOString().split('T')[0];
  const monthAgo = new Date(Date.now() - 30 * 864e5).toISOString().split('T')[0];
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? monthAgo);
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? today);
  const [statusFilter, setStatusFilter] = useState(() => savedMem?.statusFilter ?? 'All');
  const [search, setSearch] = useState(() => savedMem?.search ?? '');
  const [expanded, setExpanded] = useState(() => savedMem?.expanded ?? null); // issueId whose material breakdown is open

  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      setLoading(true);
      const branchFilter = activeBranch && activeBranch.id !== 'all' ? { branchId: activeBranch.id } : {};
      // MIP fetch is bounded server-side by the report's date pickers (Firebase
      // cost). The PRC fetch stays UNBOUNDED on purpose: it feeds the cumulative
      // "Received / WIP Left" columns (receipts against an issue may be dated
      // outside the picked range), so narrowing it would change those numbers.
      const [mip, prc] = await Promise.all([
        getTransactions(activeCompany.id, { tranTypeCode: 'MIP', ...branchFilter, fromDate: dateFrom, toDate: dateTo }),
        getTransactions(activeCompany.id, { tranTypeCode: 'PRC' }),
      ]);
      setIssues(mip);
      setReceipts(prc.filter(r => r.status !== 'Cancelled'));
    } catch (e) { console.error('Error loading material issue report:', e); }
    finally { setLoading(false); }
  }, [activeCompany, activeBranch, dateFrom, dateTo]);

  // Auto-load report: skip the initial fetch when we restored a snapshot on Back
  // (0 extra reads), but still re-fetch normally on later date changes.
  const skipInitialLoad = useRef(!!savedMem);
  useEffect(() => {
    if (skipInitialLoad.current) { skipInitialLoad.current = false; return; }
    load();
  }, [load]);

  // Persist the view snapshot (raw fetched data + filters) once data is present.
  useEffect(() => {
    if (loading) return;
    saveMemory({ dateFrom, dateTo, statusFilter, search, expanded, issues, receipts });
  }, [dateFrom, dateTo, statusFilter, search, expanded, issues, receipts, loading, saveMemory]);

  const receivedByIssue = useMemo(() => {
    const map = {};
    receipts.forEach(r => { if (r.parentId) map[r.parentId] = (map[r.parentId] || 0) + (Number(r.totalValue) || 0); });
    return map;
  }, [receipts]);

  // Per-material breakdown per issue: Issued / Consumed / Wasted / Remaining
  // (quantity), built from the linked receipts' consumedMaterials.
  const materialDetailByIssue = useMemo(() => {
    const consByIssueItem = {};
    receipts.forEach(r => {
      if (!r.parentId || !Array.isArray(r.consumedMaterials)) return;
      const bucket = consByIssueItem[r.parentId] || (consByIssueItem[r.parentId] = {});
      r.consumedMaterials.forEach(cm => {
        const b = bucket[cm.itemId] || (bucket[cm.itemId] = { consumed: 0, wasted: 0 });
        b.consumed += Number(cm.quantity) || 0;
        b.wasted += Number(cm.wastage) || 0;
      });
    });
    const map = {};
    issues.forEach(iss => {
      const cons = consByIssueItem[iss.id] || {};
      map[iss.id] = (iss.lines || []).map(l => {
        const c = cons[l.itemId] || { consumed: 0, wasted: 0 };
        const issued = Number(l.quantity) || 0;
        const consumed = round2(c.consumed);
        const wasted = round2(c.wasted);
        return {
          itemId: l.itemId,
          itemName: l.itemName || l.description || '',
          itemCode: l.itemCode || '',
          unit: l.unitOfMeasure || l.unitCode || '',
          issued, consumed, wasted,
          remaining: round2(issued - consumed - wasted)
        };
      });
    });
    return map;
  }, [issues, receipts]);

  const rows = useMemo(() => {
    return issues
      .filter(r => {
        if (statusFilter !== 'All' && r.status !== statusFilter) return false;
        if (r.docDate && (r.docDate < dateFrom || r.docDate > dateTo)) return false;
        if (search.trim()) {
          const q = search.toLowerCase();
          if (!(r.docNumber?.toLowerCase().includes(q) || r.locationName?.toLowerCase().includes(q) || r.notes?.toLowerCase().includes(q))) return false;
        }
        return true;
      })
      .map(r => {
        const issued = Number(r.totalValue) || 0;
        const received = r.status === 'Posted' ? (receivedByIssue[r.id] || 0) : 0;
        return { ...r, issued, received, remaining: Math.round((issued - received) * 100) / 100 };
      })
      .sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
  }, [issues, statusFilter, dateFrom, dateTo, search, receivedByIssue]);

  const kpis = useMemo(() => {
    const posted = rows.filter(r => r.status === 'Posted');
    return {
      count: rows.length,
      issued: posted.reduce((s, r) => s + r.issued, 0),
      received: posted.reduce((s, r) => s + r.received, 0),
      remaining: posted.reduce((s, r) => s + r.remaining, 0),
    };
  }, [rows]);

  const open = (r) => { const route = TRAN_TYPE_ROUTES[r.tranTypeCode]; if (route) navigate(route, { state: { viewTransactionId: r.id } }); };

  const exportCsv = () => {
    let csv = `Material Issue Report,${dateFrom} to ${dateTo}\n`;
    csv += `Doc No,Date,Location,Material Cost,Received,WIP Remaining,Status\n`;
    rows.forEach(r => { csv += `${r.docNumber || ''},${r.docDate || ''},"${(r.locationName || '').replace(/"/g, '""')}",${r.issued},${r.received},${r.remaining},${r.status}\n`; });
    csv += `\nTotals,,,"${kpis.issued}","${kpis.received}","${kpis.remaining}",\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `material-issue-report.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const cur = activeCompany?.currencyCode || 'PKR';

  const handlePrint = () => {
    const rowsHtml = rows.map(r => `
      <tr>
        <td>${r.docNumber || ''}</td>
        <td>${r.docDate || ''}</td>
        <td>${r.locationName || '-'}</td>
        <td class="amt">${formatAmount(r.issued)}</td>
        <td class="amt">${r.status === 'Posted' ? formatAmount(r.received) : '-'}</td>
        <td class="amt">${r.status === 'Posted' ? formatAmount(r.remaining) : '-'}</td>
        <td>${r.status}</td>
      </tr>`).join('');
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Material Issue Report</title><style>
      body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
      h1{color:#800020;font-size:20px;margin-bottom:5px}
      .meta{color:#666;font-size:12px;margin-bottom:16px}
      table{width:100%;border-collapse:collapse}
      th{background:#800020;color:#fff;padding:8px;text-align:left;font-size:11px;text-transform:uppercase}
      td{padding:7px 8px;border-bottom:1px solid #ddd}
      .amt{text-align:right;font-family:Consolas,monospace}
      tfoot td{font-weight:700;border-top:2px solid #800020}
      @media print{body{margin:12px}}
    </style></head><body>
      <h1>Material Issue Report</h1>
      <div class="meta">${activeCompany?.companyName || 'Company'} | Period: ${dateFrom} to ${dateTo}${activeBranch && activeBranch.id !== 'all' ? ' | Branch: ' + (activeBranch.branchName || '') : ''}</div>
      <table>
        <thead><tr><th>Doc No</th><th>Date</th><th>Location</th><th class="amt">Material Cost</th><th class="amt">Received</th><th class="amt">WIP Left</th><th>Status</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot><tr><td colspan="3">Totals (Posted)</td><td class="amt">${formatAmount(kpis.issued)}</td><td class="amt">${formatAmount(kpis.received)}</td><td class="amt">${formatAmount(kpis.remaining)}</td><td></td></tr></tfoot>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <PageLayout pageTitle="Material Issue Report" breadcrumb="Reports / Material Issue">
      <FiltersCard>
        <Field><label>From</label><input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></Field>
        <Field><label>To</label><input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} /></Field>
        <Field><label>Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All</option><option value="Draft">Draft</option><option value="Posted">Posted</option><option value="Cancelled">Cancelled</option>
          </select>
        </Field>
        <Field style={{ flex: 1 }}><label>Search</label>
          <SearchWrap><FiSearch /><input type="text" placeholder="Doc no, location, notes..." value={search} onChange={e => setSearch(e.target.value)} /></SearchWrap>
        </Field>
        <Btn onClick={exportCsv}><FiDownload /> CSV</Btn>
        <Btn onClick={handlePrint}><FiPrinter /> Print</Btn>
      </FiltersCard>

      <KpiGrid>
        <Kpi><span>{kpis.count}</span><label>Issues</label></Kpi>
        <Kpi><span>{cur} {formatAmount(kpis.issued)}</span><label>Material Cost (Posted)</label></Kpi>
        <Kpi><span>{cur} {formatAmount(kpis.received)}</span><label>Received as Finished Goods</label></Kpi>
        <Kpi $warn><span>{cur} {formatAmount(kpis.remaining)}</span><label>Still in Production (WIP)</label></Kpi>
      </KpiGrid>

      {loading ? <LoadingMessage>Loading...</LoadingMessage> : rows.length === 0 ? (
        <TranEmptyState><FiFileText size={48} /><h3>No Material Issues</h3><p>No records match the selected filters.</p></TranEmptyState>
      ) : (
        <TableContainer><Table><thead><tr>
          <Th style={{ width: 32 }}></Th>
          <Th>Doc No</Th><Th>Date</Th><Th>Location</Th>
          <Th style={{ textAlign: 'right' }}>Material Cost</Th>
          <Th style={{ textAlign: 'right' }}>Received</Th>
          <Th style={{ textAlign: 'right' }}>WIP Left</Th>
          <Th style={{ textAlign: 'center' }}>Status</Th>
        </tr></thead><tbody>
          {rows.map(r => {
            const mats = materialDetailByIssue[r.id] || [];
            const isOpen = expanded === r.id;
            return (
              <React.Fragment key={r.id}>
                <Tr>
                  <Td style={{ textAlign: 'center', cursor: mats.length ? 'pointer' : 'default', color: colors.text.secondary, userSelect: 'none' }} onClick={() => mats.length && setExpanded(isOpen ? null : r.id)} title={mats.length ? 'Show materials' : ''}>{mats.length ? (isOpen ? '▾' : '▸') : ''}</Td>
                  <Td><DocLink onClick={() => open(r)}>{r.docNumber} <FiExternalLink size={11} /></DocLink></Td>
                  <Td>{r.docDate}</Td>
                  <Td>{r.locationName || '-'}</Td>
                  <Td style={{ textAlign: 'right' }}>{formatAmount(r.issued)}</Td>
                  <Td style={{ textAlign: 'right' }}>{r.status === 'Posted' ? formatAmount(r.received) : '-'}</Td>
                  <Td style={{ textAlign: 'right', fontWeight: 600, color: r.remaining > 0.01 ? colors.warning.main : colors.success.main }}>{r.status === 'Posted' ? formatAmount(r.remaining) : '-'}</Td>
                  <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[r.status]}>{r.status}</StatusBadge></Td>
                </Tr>
                {isOpen && (
                  <Tr>
                    <Td colSpan={8} style={{ background: colors.neutral[50], padding: '10px 16px' }}>
                      <MatTable>
                        <thead><tr><th>Material</th><th className="r">Issued</th><th className="r">Consumed</th><th className="r">Wasted</th><th className="r">Remaining</th></tr></thead>
                        <tbody>
                          {mats.map((m, i) => (
                            <tr key={m.itemId || i}>
                              <td>{m.itemCode ? <span style={{ color: colors.primary.main, marginRight: 6 }}>{m.itemCode}</span> : null}{m.itemName}</td>
                              <td className="r">{formatAmount(m.issued)}{m.unit ? ` ${m.unit}` : ''}</td>
                              <td className="r">{formatAmount(m.consumed)}{m.unit ? ` ${m.unit}` : ''}</td>
                              <td className="r" style={{ color: m.wasted > 0 ? colors.error.main : undefined }}>{formatAmount(m.wasted)}{m.unit ? ` ${m.unit}` : ''}</td>
                              <td className="r" style={{ fontWeight: 600, color: m.remaining > 0.01 ? colors.warning.main : colors.success.main }}>{formatAmount(m.remaining)}{m.unit ? ` ${m.unit}` : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </MatTable>
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody></Table></TableContainer>
      )}
    </PageLayout>
  );
};

const FiltersCard = styled.div`background:white;border-radius:12px;box-shadow:${colors.shadows?.sm || '0 1px 3px rgba(0,0,0,0.1)'};padding:16px;margin-bottom:16px;display:flex;gap:12px;align-items:flex-end;flex-wrap:wrap;`;
const Field = styled.div`display:flex;flex-direction:column;gap:4px;label{font-size:11px;color:${colors.text.secondary};font-weight:600;text-transform:uppercase;}input,select{padding:8px 10px;border:1px solid ${colors.neutral[300]};border-radius:6px;font-size:13px;}`;
const SearchWrap = styled.div`display:flex;align-items:center;gap:6px;border:1px solid ${colors.neutral[300]};border-radius:6px;padding:0 8px;svg{color:${colors.text.secondary};}input{border:none;outline:none;padding:8px 0;width:100%;}`;
const Btn = styled.button`display:inline-flex;align-items:center;gap:6px;padding:9px 14px;background:${colors.primary.main};color:white;border:none;border-radius:6px;font-size:13px;font-weight:500;cursor:pointer;&:hover{background:${colors.primary.dark};}`;
const KpiGrid = styled.div`display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:12px;margin-bottom:16px;`;
const Kpi = styled.div`background:white;border-radius:12px;box-shadow:${colors.shadows?.sm || '0 1px 3px rgba(0,0,0,0.1)'};padding:16px;border-top:3px solid ${p => p.$warn ? colors.warning.main : colors.primary.main};span{display:block;font-size:20px;font-weight:700;color:${colors.text.primary};}label{font-size:12px;color:${colors.text.secondary};}`;
const DocLink = styled.span`color:${colors.primary.main};font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:4px;&:hover{text-decoration:underline;}`;
const MatTable = styled.table`width:100%;border-collapse:collapse;font-size:12.5px;th{text-align:left;color:${colors.text.secondary};font-weight:600;font-size:11px;text-transform:uppercase;padding:4px 8px;border-bottom:1px solid ${colors.neutral[200]};}td{padding:5px 8px;border-bottom:1px solid ${colors.neutral[100]};}.r{text-align:right;font-family:Consolas,monospace;}`;

export default MaterialIssueReport;
